/**
 * RecipientChipsInput — Empfänger-Eingabe mit Chips/Pills (To/CC/BCC).
 *
 * UX wie in einem guten Mailprogramm:
 *  - Adressen werden als kleine Pills mit ✕ angezeigt (flex-wrap, mobil-tauglich).
 *  - Übernahme der getippten Adresse via Enter, Komma, Semikolon, Tab oder Blur.
 *  - Paste mehrerer Adressen (Komma/Semikolon/Leerzeichen/Zeilenumbruch) → mehrere Chips.
 *  - Backspace bei leerem Input entfernt den letzten Chip.
 *  - Normalisierung: trim, mailto: entfernen, "Name <a@b.de>" → a@b.de, Dedupe (case-insensitive).
 *  - Ungültige Adressen werden als roter Chip markiert (bleiben editierbar, indem man sie entfernt).
 *
 * Optional: Autovervollständigung aus einer Quelle (z. B. CRM). Wird ein
 *  `suggestionsProvider` übergeben, erscheinen ab 2 Zeichen (debounced ~250 ms)
 *  passende Vorschläge in einem Dropdown unter dem Input. Auswahl per Klick oder
 *  Enter/Tab; Navigation mit ↑/↓, schließen mit Esc. Ohne den Prop verhält sich
 *  die Komponente exakt wie zuvor (keine Verhaltensänderung).
 *
 * Exportiert zusätzlich reine Helfer für Tests: parseAddresses, isValidEmail.
 */

import {
  useState, useCallback, useEffect, useRef, useId,
  type KeyboardEvent, type ClipboardEvent,
} from 'react';
import { X } from 'lucide-react';

/** Einfache, pragmatische E-Mail-Validierung (eine @, ein Punkt in der Domain). */
export function isValidEmail(addr: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((addr || '').trim());
}

/** Eine einzelne rohe Eingabe in eine saubere Adresse normalisieren. */
function normalizeAddress(raw: string): string {
  let a = (raw || '').trim();
  if (!a) return '';
  // führendes mailto: entfernen
  a = a.replace(/^mailto:/i, '').trim();
  // "Name <a@b.de>" → a@b.de
  const angle = a.match(/<([^>]+)>/);
  if (angle) a = angle[1].trim();
  // umschließende Anführungszeichen entfernen
  a = a.replace(/^["']|["']$/g, '').trim();
  return a;
}

/**
 * Roh-Input (z. B. aus Paste oder Tippen) in eine Liste sauberer Adressen
 * zerlegen. Trenner: Komma, Semikolon, Leerzeichen, Zeilenumbruch/Tab.
 * Normalisiert jede Adresse und entfernt Duplikate (case-insensitive).
 */
export function parseAddresses(input: string): string[] {
  if (!input) return [];
  const parts = input
    .split(/[\s,;\n\r\t]+/)
    .map(normalizeAddress)
    .filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    const key = p.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/** Ein Vorschlag aus der Autocomplete-Quelle. */
export interface RecipientSuggestion {
  label: string;
  email: string;
}

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  id?: string;
  label?: string;
  /**
   * Optionaler Provider für Empfänger-Vorschläge (z. B. CRM). Wird bei jeder
   * Eingabe ab 2 Zeichen (debounced) aufgerufen. Liefert eine Liste mit
   * Anzeige-Label und E-Mail-Adresse. Ohne diesen Prop ist die Autocomplete aus.
   */
  suggestionsProvider?: (query: string) => Promise<RecipientSuggestion[]>;
}

export default function RecipientChipsInput({ value, onChange, placeholder, id, label, suggestionsProvider }: Props) {
  const [text, setText] = useState('');

  // ── Autocomplete-State ──────────────────────────────────────────────────
  const [suggestions, setSuggestions] = useState<RecipientSuggestion[]>([]);
  const [openSug, setOpenSug] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const reqSeq = useRef(0);          // verhindert veraltete Antworten (race)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoId = useId();
  const listboxId = `${id ?? autoId}-listbox`;

  /** Aktuelle Werte mit neuen Adressen mergen (case-insensitive dedupe). */
  const commit = useCallback(
    (raw: string) => {
      const incoming = parseAddresses(raw);
      if (incoming.length === 0) return;
      const existing = new Set(value.map((v) => v.toLowerCase()));
      const merged = [...value];
      for (const a of incoming) {
        if (!existing.has(a.toLowerCase())) {
          existing.add(a.toLowerCase());
          merged.push(a);
        }
      }
      if (merged.length !== value.length) onChange(merged);
    },
    [value, onChange],
  );

  const removeAt = useCallback(
    (idx: number) => {
      const next = value.filter((_, i) => i !== idx);
      onChange(next);
    },
    [value, onChange],
  );

  /** Einen Vorschlag übernehmen → Chip einfügen, Input leeren, Dropdown zu. */
  const acceptSuggestion = useCallback(
    (s: RecipientSuggestion) => {
      commit(s.email);
      setText('');
      setSuggestions([]);
      setOpenSug(false);
      setActiveIdx(-1);
    },
    [commit],
  );

  // ── Debounced Vorschläge laden ──────────────────────────────────────────
  useEffect(() => {
    if (!suggestionsProvider) return;
    const q = text.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setOpenSug(false);
      setActiveIdx(-1);
      return;
    }
    const seq = ++reqSeq.current;
    const t = setTimeout(async () => {
      try {
        const res = await suggestionsProvider(q);
        if (seq !== reqSeq.current) return; // veraltete Antwort verwerfen
        setSuggestions(res);
        setOpenSug(res.length > 0);
        setActiveIdx(-1);
      } catch {
        if (seq !== reqSeq.current) return;
        setSuggestions([]);
        setOpenSug(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [text, suggestionsProvider]);

  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Tastatur-Navigation im Vorschlags-Dropdown hat Vorrang.
    if (openSug && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpenSug(false);
        setActiveIdx(-1);
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && activeIdx >= 0) {
        e.preventDefault();
        acceptSuggestion(suggestions[activeIdx]);
        return;
      }
    }

    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') {
      if (text.trim()) {
        // Tab nur abfangen, wenn etwas zu übernehmen ist — sonst normales Tabbing.
        e.preventDefault();
        commit(text);
        setText('');
        setOpenSug(false);
      }
    } else if (e.key === 'Backspace' && text === '' && value.length > 0) {
      e.preventDefault();
      removeAt(value.length - 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text');
    if (/[\s,;]/.test(pasted)) {
      e.preventDefault();
      commit((text ? text + ' ' : '') + pasted);
      setText('');
      setOpenSug(false);
    }
  };

  const handleBlur = () => {
    // Verzögern, damit ein Klick auf einen Vorschlag noch greift.
    blurTimer.current = setTimeout(() => {
      if (text.trim()) {
        commit(text);
        setText('');
      }
      setOpenSug(false);
      setActiveIdx(-1);
    }, 120);
  };

  return (
    <div className="block text-[11px] font-medium text-gray-500">
      {label}
      <div className="relative">
        <div
          className="mt-0.5 w-full flex flex-wrap items-center gap-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus-within:ring-2 focus-within:ring-[#9B1C1C]/20"
          onClick={() => document.getElementById(id ?? '')?.focus()}
        >
          {value.map((addr, idx) => {
            const invalid = !isValidEmail(addr);
            return (
              <span
                key={`${addr}-${idx}`}
                title={invalid ? 'Ungültige Adresse' : undefined}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] max-w-full ${
                  invalid
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                <span className="truncate">{addr}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAt(idx);
                  }}
                  title="Entfernen"
                  aria-label={`${addr} entfernen`}
                  className="opacity-60 hover:opacity-100 flex-shrink-0"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
          <input
            id={id}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={handleBlur}
            placeholder={value.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[120px] bg-transparent outline-none py-0.5 font-normal text-gray-900 placeholder:text-gray-400"
            type="text"
            autoComplete="off"
            role={suggestionsProvider ? 'combobox' : undefined}
            aria-expanded={suggestionsProvider ? openSug : undefined}
            aria-controls={suggestionsProvider && openSug ? listboxId : undefined}
            aria-autocomplete={suggestionsProvider ? 'list' : undefined}
            aria-activedescendant={
              suggestionsProvider && openSug && activeIdx >= 0 ? `${listboxId}-opt-${activeIdx}` : undefined
            }
          />
        </div>

        {/* Vorschlags-Dropdown (nur wenn ein Provider gesetzt ist) */}
        {suggestionsProvider && openSug && suggestions.length > 0 && (
          <ul
            id={listboxId}
            role="listbox"
            className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1"
          >
            {suggestions.map((s, i) => (
              <li
                key={`${s.email}-${i}`}
                id={`${listboxId}-opt-${i}`}
                role="option"
                aria-selected={i === activeIdx}
                onMouseDown={(e) => {
                  // mousedown vor blur → Auswahl bleibt erhalten
                  e.preventDefault();
                  acceptSuggestion(s);
                }}
                onMouseEnter={() => setActiveIdx(i)}
                className={`px-3 py-1.5 cursor-pointer text-sm font-normal ${
                  i === activeIdx ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                }`}
              >
                <span className="truncate block">{s.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
